import { useCallback, useMemo, useRef, useState } from "react";
import type { ChangeEvent, DragEvent, FormEvent } from "react";
import { ArrowLeftRegular, DocumentPdfRegular } from "@fluentui/react-icons";
import { useDocuparserUpload } from "../hooks/useDocuparserUpload";
import {
  ALLOWED_FILE_TYPE,
  FILE_DIRECTORY_SEPARATOR_REGEX,
  FILE_INVALID_CHARS_REGEX,
  FILE_NAME_REGEX,
  FILE_PATH_TRAVERSAL_REGEX,
  SAFE_NAME_REGEX,
  UPLOAD_ERROR_MESSAGES,
  VALIDATION_LIMITS,
} from "../../../constants";
import { ErrorPopup } from "./ErrorPopup";
import { SuccessPopup } from "./SuccessPopup";
import styles from "./UploadView.module.css";

interface FormValues {
  name: string;
  description: string;
  file: File | null;
}

interface FormErrors {
  name?: string[];
  file?: string[];
  description?: string;
}

function isValidFileName(filename: string): boolean {
  return FILE_NAME_REGEX.test(filename);
}

function isValidDocumentName(documentName: string): boolean {
  return SAFE_NAME_REGEX.test(documentName);
}

function isValidFileSize(fileSize: number): boolean {
  return fileSize <= VALIDATION_LIMITS.MAX_FILE_SIZE_MB * 1024 * 1024;
}

function isValidPdfType(file: File): boolean {
  return file.type === ALLOWED_FILE_TYPE || file.name.toLowerCase().endsWith(".pdf");
}

function validateFile(file: File): string[] {
  const errors: string[] = [];
  if (!isValidFileSize(file.size)) errors.push(UPLOAD_ERROR_MESSAGES.FILE_TOO_LARGE);
  if (!isValidPdfType(file)) errors.push(UPLOAD_ERROR_MESSAGES.INVALID_FILE_TYPE);
  if (FILE_INVALID_CHARS_REGEX.test(file.name)) errors.push(UPLOAD_ERROR_MESSAGES.FILE_INVALID_CHARS);
  if (FILE_PATH_TRAVERSAL_REGEX.test(file.name)) errors.push(UPLOAD_ERROR_MESSAGES.FILE_PATH_TRAVERSAL);
  if (FILE_DIRECTORY_SEPARATOR_REGEX.test(file.name)) errors.push(UPLOAD_ERROR_MESSAGES.FILE_DIRECTORY_COMPONENT);
  if (!isValidFileName(file.name)) errors.push(UPLOAD_ERROR_MESSAGES.INVALID_FILE_NAME);
  return errors;
}

function validateFormValues(values: FormValues): FormErrors {
  const errors: FormErrors = {};

  if (values.name.trim()) {
    const nameErrors: string[] = [];
    if (values.name.length > VALIDATION_LIMITS.MAX_NAME_LENGTH) nameErrors.push(UPLOAD_ERROR_MESSAGES.NAME_TOO_LONG);
    if (!isValidDocumentName(values.name)) nameErrors.push(UPLOAD_ERROR_MESSAGES.NAME_INVALID_CHARS);
    if (nameErrors.length > 0) errors.name = nameErrors;
  } else {
    errors.name = [UPLOAD_ERROR_MESSAGES.NAME_REQUIRED];
  }

  if (values.file) {
    const fileErrors = validateFile(values.file);
    if (fileErrors.length > 0) errors.file = fileErrors;
  } else {
    errors.file = [UPLOAD_ERROR_MESSAGES.FILE_REQUIRED];
  }

  if (values.description.length > VALIDATION_LIMITS.MAX_DESCRIPTION_LENGTH) {
    errors.description = UPLOAD_ERROR_MESSAGES.DESCRIPTION_TOO_LONG;
  }

  return errors;
}

interface UploadViewProps {
  readonly onDone: () => void;
}

export function UploadView({ onDone }: Readonly<UploadViewProps>) {
  const { uiState, submit, setDragging, resetProgress, closeSuccessPopup, closeErrorPopup } = useDocuparserUpload();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const successDialogRef = useRef<HTMLDialogElement>(null);
  const errorDialogRef = useRef<HTMLDialogElement>(null);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const errors = useMemo(() => validateFormValues({ name, description, file }), [name, description, file]);
  const showNameError = submitAttempted && !!errors.name;
  const showFileError = (submitAttempted && !!errors.file) || (!!errors.file && !!file);
  const showDescriptionError = submitAttempted && !!errors.description;

  const resetForm = useCallback(() => {
    setName("");
    setDescription("");
    setFile(null);
    setSubmitAttempted(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, []);

  const handleSuccessPopupOk = useCallback(() => {
    closeSuccessPopup();
    resetForm();
    onDone();
  }, [closeSuccessPopup, resetForm, onDone]);

  const handleErrorPopupOk = useCallback(() => {
    closeErrorPopup();
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    resetProgress();
  }, [closeErrorPopup, resetProgress]);

  const handleDragOver = useCallback(
    (e: DragEvent<HTMLButtonElement>) => {
      e.preventDefault();
      if (!uiState.isUploading) setDragging(true);
    },
    [uiState.isUploading, setDragging],
  );

  const handleDragLeave = useCallback(
    (e: DragEvent<HTMLButtonElement>) => {
      e.preventDefault();
      setDragging(false);
    },
    [setDragging],
  );

  const handleDrop = useCallback(
    (e: DragEvent<HTMLButtonElement>) => {
      e.preventDefault();
      setDragging(false);
      if (uiState.isUploading) return;
      const dropped = e.dataTransfer.files?.[0];
      if (dropped) setFile(dropped);
    },
    [uiState.isUploading, setDragging],
  );

  const handleFileSelect = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (selected) setFile(selected);
    e.target.value = "";
  }, []);

  const handleBrowseClick = useCallback(() => {
    if (!uiState.isUploading) fileInputRef.current?.click();
  }, [uiState.isUploading]);

  const handleRemoveFile = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      if (uiState.isUploading) return;
      setFile(null);
      resetProgress();
      if (fileInputRef.current) fileInputRef.current.value = "";
    },
    [uiState.isUploading, resetProgress],
  );

  const handleSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      setSubmitAttempted(true);
      if (!file || Object.keys(validateFormValues({ name, description, file })).length > 0) return;
      await submit(file, name, description);
    },
    [submit, file, name, description],
  );

  const hasFileError = showFileError;

  return (
    <div className={styles.uploadPage}>
      <div className={styles.uploadContainer}>
        <div className={styles.uploadHeader}>
          <button
            type="button"
            className={styles.backButton}
            onClick={onDone}
            aria-label="Go back to document list"
            disabled={uiState.isUploading}
          >
            <ArrowLeftRegular fontSize={20} />
          </button>
          <h2 className={styles.uploadTitle}>Upload</h2>
        </div>

        <form onSubmit={handleSubmit} noValidate aria-label="Document upload form">
          <div className={styles.formGroup}>
            <label htmlFor="docuparser-name" className={styles.formLabel}>
              Name<span className={styles.required}>*</span>
            </label>
            <input
              id="docuparser-name"
              type="text"
              className={`${styles.input} ${showNameError ? styles.inputError : ""}`}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter document name"
              disabled={uiState.isUploading}
            />
            {showNameError && (
              <div className={styles.errorMessagesContainer} role="alert">
                {errors.name!.map((msg) => (
                  <span key={msg} className={styles.errorMessage}>
                    {msg}
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="docuparser-description" className={styles.formLabel}>
              Description
            </label>
            <textarea
              id="docuparser-description"
              className={`${styles.input} ${styles.textarea} ${showDescriptionError ? styles.inputError : ""}`}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter description"
              rows={4}
              disabled={uiState.isUploading}
            />
            {showDescriptionError && (
              <span className={styles.errorMessage} role="alert">
                {errors.description}
              </span>
            )}
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="docuparser-pdf-upload" className={styles.formLabel}>
              PDF File<span className={styles.required}>*</span>
            </label>

            <button
              type="button"
              className={[
                styles.dropZone,
                uiState.isDragging ? styles.dragging : "",
                file ? styles.hasFile : "",
                hasFileError ? styles.errorBorder : "",
                uiState.isUploading ? styles.uploading : "",
              ]
                .filter(Boolean)
                .join(" ")}
              tabIndex={uiState.isUploading ? -1 : 0}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={handleBrowseClick}
              aria-label="Upload PDF file – click or drag and drop"
              disabled={uiState.isUploading}
            >
              <input
                id="docuparser-pdf-upload"
                ref={fileInputRef}
                type="file"
                accept=".pdf"
                onChange={handleFileSelect}
                className={styles.hiddenFileInput}
                disabled={uiState.isUploading}
              />

              {file ? (
                <div className={styles.filePreview}>
                  <DocumentPdfRegular fontSize={24} primaryFill="#e74c3c" aria-hidden="true" />
                  <div className={styles.fileDetails}>
                    <p className={styles.fileName}>{file.name}</p>
                    <p className={styles.fileSize}>{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                  </div>
                  {!uiState.isUploading && (
                    <button type="button" className={styles.removeBtn} onClick={handleRemoveFile} aria-label="Remove file">
                      ×
                    </button>
                  )}
                </div>
              ) : (
                <div className={styles.dropZoneContent}>
                  <p className={styles.dropText}>Drag &amp; Drop PDF here</p>
                  <p className={styles.dropTextAlt}>or click to browse</p>
                  <p className={styles.dropText}>Maximum file size: {VALIDATION_LIMITS.MAX_FILE_SIZE_MB} MB</p>
                </div>
              )}

              {uiState.isUploading && uiState.uploadProgress > 0 && (
                <div className={styles.uploadProgressContainer}>
                  <progress
                    className={styles.progressBar}
                    value={uiState.uploadProgress}
                    max={100}
                    aria-label={`Upload progress: ${uiState.uploadProgress}%`}
                  >
                    {uiState.uploadProgress}%
                  </progress>
                  <p className={styles.progressText}>{uiState.uploadProgress}% uploaded</p>
                </div>
              )}

              {showFileError && !uiState.isUploading && (
                <div className={styles.errorMessagesContainer} role="alert">
                  {errors.file!.map((msg) => (
                    <span key={msg} className={styles.errorMessage}>
                      {msg}
                    </span>
                  ))}
                </div>
              )}
            </button>
          </div>

          <div className={styles.btnWrapper}>
            <button
              type="submit"
              className={styles.convertBtn}
              disabled={uiState.isUploading}
              aria-label={uiState.isUploading ? "Processing upload" : "Convert document"}
            >
              {uiState.isUploading ? "Processing..." : "Convert"}
            </button>
          </div>
        </form>
      </div>

      {uiState.showSuccessPopup && <SuccessPopup dialogRef={successDialogRef} onOk={handleSuccessPopupOk} />}
      {uiState.showErrorPopup && (
        <ErrorPopup
          dialogRef={errorDialogRef}
          errorMessage={uiState.uploadError}
          detailedMessages={uiState.detailedMessages}
          onOk={handleErrorPopupOk}
        />
      )}
    </div>
  );
}
